<template>
  <div
    v-if="panoObj"
    :class="slideoutClasses"
  >
    <!-- Toggle -->
    <button
      id="slideout-modal-toggle"
      :class="slideoutToggleClasses"
      @click="$emit('toggleSlideout')"
      :title="htmlDecode(slideoutToggleTitle)"
      :aria-label="htmlDecode(slideoutToggleTitle)"
      :tabindex="showSlideout ? '' : '-1'"
    >
      <SvgIcon
        class="w-3 h-5 transition-transform duration-1000 ease-in-out"
        :class="{ 'transform-rotate-y-180': !showSlideout }"
        icon="chevron-left"
      />
    </button>
    <!-- Container -->
    <div :class="containerClasses">
      <!-- Scrolling -->
      <transition
        name="fade"
        mode="out-in"
      >
        <div
          :key="`slideout-${panoObj.slug}-${isGuidedTour ? 'gt' : 'group'}`"
          class="relative w-full max-h-full overflow-auto scrolling-touch"
          :style="{ maxHeight: `${windowDimensions.height - 140}px` }"
        >
          <header :class="headerClasses">
            <h1
              :class="headingClasses"
              v-html="panoObj.title"
            />
            <p
              v-if="subtitle"
              :class="subheadingClasses"
              v-html="subtitle"
            />
          </header>
          <div
            v-if="hasContent"
            class="px-4 py-2_5"
          >
            <!-- GT description -->
            <div
              v-if="tourDescriptionText"
              class="mb-1 last:mb-0"
            >
              <p
                :class="tourDesciptionClasses"
                v-html="tourDescriptionText"
              />
            </div>
            <!-- Contentspots -->
            <ul
              v-if="prevTour || usedArr( contentspots ) || nextTour"
              class=""
            >
              <!-- Prev -->
              <SlideoutLi
                v-if="(prevTour || {}).slug"
                :post="prevTour"
                :routeTo="{
                  name: routeName,
                  params: { panorama_id: prevTour.slug }
                }"
                :allow-tabbing="showSlideout"
                icon="chevron-left"
              />
              <!-- Contentpots -->
              <SlideoutLi
                v-for="csObj in contentspots"
                :key="`tour-${panoObj.id}-contenspot-${csObj.id}`"
                :post="csObj"
                :routeTo="csObj.routeTo"
                :allow-tabbing="showSlideout"
                is-contentspot
                :icon="csObj.type"
              />
              <!-- Next -->
              <SlideoutLi
                v-if="(nextTour || {}).slug"
                :post="nextTour"
                :routeTo="{
                  name: routeName,
                  params: { panorama_id: nextTour.slug }
                }"
                :allow-tabbing="showSlideout"
                icon="chevron-right"
              />
            </ul>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
import Heading from '@/components/text/Heading.vue'
import Subheading from '@/components/text/Subheading.vue'
import SlideoutLi from '@/components/SlideoutLi.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'Slideout',

  components: {
    SvgIcon,
    Heading,
    Subheading,
    SlideoutLi
  },

  props: {
    tourGroup: {
      type: Object,
      default: () => {}
    },
    groupTours: {
      type: Array,
      default: () => []
    },
    panoObj: {
      type: Object,
      default: () => {}
    },
    contentspots: {
      type: Array,
      default: () => []
    },
    prevTour: {
      type: Object,
      default: () => {}
    },
    nextTour: {
      type: Object,
      default: () => {}
    },
    showSlideout: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Data
    ...mapState([ 'windowDimensions' ]),
    ...mapGetters([
      'routeName',
      'isGuidedTour',
      'myStore'
    ]),
    hasContent() {
      const { contentspots, prevTour, nextTour, tourDescriptionText, usedArr } = this
      return tourDescriptionText || prevTour || usedArr( contentspots ) || nextTour
    },
    //
    slideoutStyles() { return this.myStore(['styles', 'panorama_screen', 'slideout']) ?? {} },
    slideoutClasses() {
      const xValue = this.showSlideout ? 0 : '-full'
      const yValue = this.hasContent ? '-1/2' : 0
      const topValue = this.hasContent ? '1/2' : 0
      return [
        // Position
        'absolute',
        `left-0 top-${topValue}`,
        // Transform
        `transform-translate-x-${xValue}-y-${yValue}`,
        // Dimension
        'w-64 md:w-80 max-w-70%',
        // Transitions
        'transition-transform duration-500 ease-in-out'
      ]
    },
    // Toggle
    slideoutToggleClasses() {
      const toggleStyles = this.slideoutStyles.toggle ?? {}
      const focusStyles = toggleStyles.focusStyles ?? {}
      const hoverStyles = toggleStyles.hoverStyles ?? {}
      // Return
      return [
        // GTM
        `slideout-modal-${this.showSlideout ? 'open' : 'closed'}`,
        // Position
        'absolute',
        'left-full top-1/2',
        'transform-translate-x-0-y--1/2',
        // Dimensions
        'px-2 py-3',
        // Border
        `rounded-r-${toggleStyles.border_radius ?? 5}`,
        // Bg
        `bg-${toggleStyles.bg_color ?? 'white'}`,
        `focus:bg-${focusStyles.bg_color ?? 'black'} lg:hover:bg-${hoverStyles.bg_color ?? 'accent-1'}`,
        `fill-${toggleStyles.fill_color ?? 'accent-1'}`,
        `focus:fill-${focusStyles.fill_color ?? 'white'} lg:hover:fill-${hoverStyles.fill_color ?? 'white'}`,
        'transition-colors duration-500 ease-in-out',
        'focus:outline-none'
      ]
    },
    slideoutToggleTitle() { return this.myStore(['labels', 'panorama_screen', 'slideout', 'toggle', 'btn_title']) ?? 'Toggle slideout menu' },
    // Container
    containerClasses() {
      const containerStyles = this.slideoutStyles.container ?? {}
      // Return
      return [
        // Position / Dimensions
        'r-cover',
        'overflow-hidden',
        // Border
        `rounded-${this.hasContent ? 'r' : 'br'}-${containerStyles.border_radius ?? 5}`,
        // Background
        `bg-${containerStyles.bg_color ?? 'white'}`,
      ]
    },
    headerClasses() {
      const topStyles = this.slideoutStyles.top ?? {}
      // Return
      return [
        // Padding
        'px-6 py-5',
        // Background
        `bg-${topStyles.bg_color ?? 'off-white'}`,
      ]
    },
    // Text
    headingClasses() {
      // Data
      const settings = this.slideoutStyles.heading ?? {}
      const textColor = settings.text_color ?? 'accent-1'
      const fontFamily = settings.font_family ?? 'heading'
      const fontWeight = `${fontFamily}-${settings.font_weight ?? 'bold'}`
      const textTransform = settings.text_transform ?? 'normal-case'
      // Return
      return [
        // Margins
        'mb-1 last:mb-0',
        // Color
        `text-${textColor}`,
        // Size
        'text-lg md:text-xl',
        // Line height
        'leading-thin',
        // Family
        `font-${fontFamily}`,
        // Weight
        `font-${fontWeight}`,
        // Transform
        textTransform
      ]
    },
    subheadingClasses() {
      // Data
      const settings = this.slideoutStyles.subheading ?? {}
      const textColor = settings.text_color ?? 'accent-1'
      const fontFamily = settings.font_family ?? 'heading'
      const fontWeight = `${fontFamily}-${settings.font_weight ?? 'bold'}`
      const textTransform = settings.text_transform ?? 'normal-case'
      // Return
      return [
        // Margins
        'mb-1 last:mb-0',
        // Color
        `text-${textColor}`,
        // Line height
        'leading-thin',
        // Family
        `font-${fontFamily}`,
        // Weight
        `font-${fontWeight}`,
        // Transform
        textTransform
      ]
    },
    subtitle() {
      if (this.tourGroup && this.tourGroup.title) { return this.tourGroup.title }
      if (this.isGuidedTour) {
        const text = 'Guided tour'
        const tourIndex = this.groupTours.map(({ slug }) => slug).indexOf(this.panoObj.slug)
        const groupToursLength = this.groupTours.length
        return `${text} &mdash; ${tourIndex + 1}/${groupToursLength}`
      }
      return
    },
    // Guided Tour Text
    gtPanoObj() { return (this.groupTours || []).find(({ slug }) => this.panoObj.slug === slug) ?? {} },
    tourDescriptionText() {
      const { gtPanoObj } = this
      const gtTourDescription = gtPanoObj.gt_description ?? ''
      return gtTourDescription
    },
    tourDesciptionClasses() {
      // Data
      const settings = this.slideoutStyles.tour_description ?? {}
      const textColor = settings.text_color ?? 'body'
      const fontFamily = settings.font_family ?? 'body'
      const fontWeight = `${fontFamily}-${settings.font_weight ?? 'normal'}`
      const textTransform = settings.text_transform ?? 'normal-case'
      // Return
      return [
        // Padding
        'p-2',
        // Color
        `text-${textColor}`,
        // Family
        `font-${fontFamily}`,
        // Weight
        `font-${fontWeight}`,
        // Transform
        textTransform
      ]
    }
  }
}
</script>
